import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    ListTreatmentAssignmentRulesParams,
    TreatmentAssignmentRule,
    TreatmentAssignmentRuleMatch,
    TreatmentAssignmentRuleSaveModel,
} from '../model/treatment-assignment-rule';

const basePath = '/api/treatment-assignment-rules';

/**
 * HTTP client for treatment auto-assignment rules. Reads need read:treatment-assign-rule; writes need
 * write:treatment-assign-rule.
 */
@Injectable({ providedIn: 'root' })
export class TreatmentAssignmentRuleClient {
    private readonly _httpClient = inject(HttpClient);

    list(params: ListTreatmentAssignmentRulesParams): Observable<TreatmentAssignmentRule[]> {
        let httpParams = new HttpParams();
        if (params.treatmentTypeOriginalId != null) {
            httpParams = httpParams.set('treatmentTypeOriginalId', String(params.treatmentTypeOriginalId));
        }
        if (params.isActive != null) {
            httpParams = httpParams.set('isActive', String(params.isActive));
        }
        if (params.search != null && params.search.length > 0) {
            httpParams = httpParams.set('search', params.search);
        }
        if (params.designerUserId != null && params.designerUserId.length > 0) {
            httpParams = httpParams.set('designerUserId', params.designerUserId);
        }
        return this._httpClient.get<TreatmentAssignmentRule[]>(basePath, { params: httpParams });
    }

    getById(id: number): Observable<TreatmentAssignmentRule> {
        return this._httpClient.get<TreatmentAssignmentRule>(`${basePath}/${id}`);
    }

    create(model: TreatmentAssignmentRuleSaveModel): Observable<TreatmentAssignmentRule> {
        return this._httpClient.post<TreatmentAssignmentRule>(basePath, model);
    }

    update(id: number, model: TreatmentAssignmentRuleSaveModel): Observable<TreatmentAssignmentRule> {
        return this._httpClient.put<TreatmentAssignmentRule>(`${basePath}/${id}`, model);
    }

    setActive(id: number, isActive: boolean): Observable<TreatmentAssignmentRule> {
        return this._httpClient.patch<TreatmentAssignmentRule>(`${basePath}/${id}/active`, isActive);
    }

    delete(id: number): Observable<void> {
        return this._httpClient.delete<void>(`${basePath}/${id}`);
    }

    /** Which rule a real treatment would match - the way to check a configuration before trusting it. */
    previewMatch(treatmentId: string): Observable<TreatmentAssignmentRuleMatch> {
        return this._httpClient.post<TreatmentAssignmentRuleMatch>(`${basePath}/preview-match`, { treatmentId });
    }
}
